// After a person confirms a card that emails a customer, supplier or job worker (the server marks each such card with
// its email: a quotation, an acknowledgement, a payment reminder, a dispatch notice, a purchase order, a job worker's
// chase), the email waits UNDO_SECONDS on the server before it goes (#512): this says so with a countdown and an Undo,
// in chat and on Today. Only when the Confirm's reply says its email was queued is the countdown shown (#718): when
// nothing goes (it could not be queued, or the Counterparty has no email), there is nothing to undo (#686).
import { useEffect, useRef, useState } from "react";
import type { ActReply, Card } from "../api.js";
import { useWords } from "../i18n/words.js";
import { UNDO_SECONDS } from "../preview/ActionPreview.js";

/** An email to a customer waiting out its undo window. */
export interface PendingEmail {
  readonly run: string;
  readonly workItem: string;
  readonly customer: string;
}

/** The email a confirmed card sends a Counterparty, when its reply says the email was queued. */
export function emailOf(card: Pick<Card, "run" | "workItem" | "email">, reply: ActReply): PendingEmail | undefined {
  const email = card.email;
  if (!email?.to || reply.emailQueued !== true) return undefined;
  return { run: card.run, workItem: card.workItem, customer: email.counterparty };
}

export function UndoEmail({
  pending,
  onUndo,
  onGone,
}: {
  pending: PendingEmail;
  /** Asks the server to stop it: true when it did. */
  onUndo: (pending: PendingEmail) => Promise<boolean>;
  /** The countdown ran out: the email has gone. */
  onGone: () => void;
}) {
  const w = useWords();
  const [left, setLeft] = useState(UNDO_SECONDS);
  const [notice, setNotice] = useState<string | undefined>(undefined);
  const button = useRef<HTMLButtonElement>(null);
  useEffect(() => button.current?.focus(), []);
  useEffect(() => {
    if (notice !== undefined) return;
    if (left === 0) {
      onGone();
      return;
    }
    const tick = setTimeout(() => setLeft(left - 1), 1000);
    return () => clearTimeout(tick);
  }, [left, notice, onGone]);

  if (notice !== undefined) return <p role="status">{notice}</p>;
  return (
    <div className="undo" role="status">
      <span>{w.previewSendingIn(pending.customer, left)}</span>
      <button
        ref={button}
        type="button"
        className="btn"
        onClick={() =>
          void onUndo(pending).then(
            (undone) => setNotice(undone ? w.emailUndone(pending.customer) : w.emailAlreadySent(pending.customer)),
            () => setNotice(w.emailAlreadySent(pending.customer)),
          )
        }
      >
        {w.previewUndo}
      </button>
    </div>
  );
}
